import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import EventHero from './components/EventHero';
import EventDescription from './components/EventDescription';
import EventLocation from './components/EventLocation';
import OrganizerCard from './components/OrganizerCard';
import EventInfoSidebar from './components/EventInfoSidebar';

interface EventData {
  id: string;
  title: string;
  description: string | null;
  category: string;
  start_date: string;
  end_date: string | null;
  location_type: string;
  location_address: string | null;
  location_city: string | null;
  location_country: string | null;
  location_url: string | null;
  location_reveal_date: string | null;
  cover_image: string | null;
  price: number | null;
  currency: string | null;
  capacity: number | null;
  social_links: any;
  profiles: {
    full_name: string | null;
    email: string;
    avatar_url: string | null;
  };
  registrations?: { count: number }[];
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://eventshowcase.de';

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState<EventData | null>(null);
  const [loading, setLoading] = useState(true);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);

  useEffect(() => {
    if (id) fetchEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (id && user) {
      checkRegistration();
    } else {
      setAlreadyRegistered(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  // SEO: inject Schema.org Event JSON-LD when event data is available
  useEffect(() => {
    if (!event) return;

    const organizerName =
      event.profiles?.full_name ||
      event.profiles?.email?.split('@')[0] ||
      'Unknown';

    const eventUrl = `${SITE_URL}/event/${event.id}`;
    const eventTitle = `${event.title} | EVENTSHOWCASE`;
    const eventDescription = event.description
      ? event.description.slice(0, 155)
      : `Join ${event.title} on EVENTSHOWCASE.${event.location_city ? ` Located in ${event.location_city}.` : ''}`;
    const eventImage = event.cover_image || `${SITE_URL}/og-default.jpg`;

    // Update page title
    document.title = eventTitle;

    // Helper to set or create a meta tag
    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        const [attrName, attrVal] = selector.replace('meta[', '').replace(']', '').split('=');
        el.setAttribute(attrName.trim(), attrVal.replace(/"/g, '').trim());
        document.head.appendChild(el);
        el.setAttribute('data-event-meta', 'true');
      }
      el.setAttribute(attr, value);
    };

    // Standard description
    setMeta('meta[name="description"]', 'content', eventDescription);

    // Open Graph tags — used by WhatsApp, Facebook, LinkedIn, Telegram
    setMeta('meta[property="og:title"]', 'content', eventTitle);
    setMeta('meta[property="og:description"]', 'content', eventDescription);
    setMeta('meta[property="og:image"]', 'content', eventImage);
    setMeta('meta[property="og:url"]', 'content', eventUrl);
    setMeta('meta[property="og:type"]', 'content', 'event');
    setMeta('meta[property="og:site_name"]', 'content', 'EVENTSHOWCASE');

    // Twitter Card tags
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', eventTitle);
    setMeta('meta[name="twitter:description"]', 'content', eventDescription);
    setMeta('meta[name="twitter:image"]', 'content', eventImage);

    // Schema.org JSON-LD
    const locationObj: Record<string, any> =
      event.location_type === 'online'
        ? { '@type': 'VirtualLocation', url: event.location_url || SITE_URL }
        : {
            '@type': 'Place',
            name: event.location_address || event.location_city || '',
            address: {
              '@type': 'PostalAddress',
              addressLocality: event.location_city || '',
              addressCountry: event.location_country || '',
            },
          };

    const schema: Record<string, any> = {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: event.title,
      description: event.description || '',
      startDate: event.start_date,
      endDate: event.end_date || event.start_date,
      url: eventUrl,
      image: eventImage,
      location: locationObj,
      organizer: {
        '@type': 'Person',
        name: organizerName,
      },
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode:
        event.location_type === 'online'
          ? 'https://schema.org/OnlineEventAttendanceMode'
          : 'https://schema.org/OfflineEventAttendanceMode',
    };

    if (event.price !== null && event.price !== undefined) {
      schema.offers = {
        '@type': 'Offer',
        price: event.price,
        priceCurrency: event.currency || 'USD',
        availability: 'https://schema.org/InStock',
        url: `${eventUrl}/register`,
      };
    }

    const existing = document.getElementById('schema-event');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'schema-event';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      // Restore default title and meta tags on unmount
      document.title = 'EVENTSHOWCASE \u2013 Discover Events & Grow Your Social Following';

      const restoreDefaults: Array<[string, string, string]> = [
        ['meta[name="description"]', 'content', 'EVENTSHOWCASE is the premier event discovery platform. Find free and paid events, boost social media engagement, and connect with event organizers worldwide.'],
        ['meta[property="og:title"]', 'content', 'EVENTSHOWCASE \u2013 Discover Events & Grow Your Social Following'],
        ['meta[property="og:description"]', 'content', 'Find free and paid events, boost social media engagement, and connect with event organizers worldwide on EVENTSHOWCASE.'],
        ['meta[property="og:image"]', 'content', ''],
        ['meta[property="og:url"]', 'content', 'https://eventshowcase.de/'],
        ['meta[property="og:type"]', 'content', 'website'],
        ['meta[name="twitter:title"]', 'content', 'EVENTSHOWCASE \u2013 Discover Events & Grow Your Social Following'],
        ['meta[name="twitter:description"]', 'content', 'Find free and paid events, boost social media engagement, and connect with event organizers worldwide.'],
        ['meta[name="twitter:image"]', 'content', ''],
      ];

      restoreDefaults.forEach(([selector, attr, value]) => {
        const el = document.querySelector(selector);
        if (el) el.setAttribute(attr, value);
      });

      // Remove any dynamically created meta tags
      document.querySelectorAll('meta[data-event-meta="true"]').forEach(el => el.remove());

      const schemaEl = document.getElementById('schema-event');
      if (schemaEl) schemaEl.remove();
    };
  }, [event]);

  const checkRegistration = async () => {
    if (!id || !user) return;
    try {
      const { data } = await supabase
        .from('registrations')
        .select('id')
        .eq('event_id', id)
        .eq('user_id', user.id)
        .maybeSingle();
      setAlreadyRegistered(!!data);
    } catch (error) {
      console.error('Error checking registration:', error);
    }
  };

  const fetchEvent = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data: eventData, error: eventError } = await supabase
        .from('events')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (eventError) throw eventError;
      
      if (!eventData) {
        setEvent(null);
        setLoading(false);
        return;
      }

      // Fetch organizer profile separately
      const { data: profileData } = await supabase
        .from('profiles')
        .select('full_name, email, avatar_url')
        .eq('id', eventData.organizer_id)
        .maybeSingle();

      // Fetch registration count separately
      const { count } = await supabase
        .from('registrations')
        .select('*', { count: 'exact', head: true })
        .eq('event_id', id);

      // Combine the data
      setEvent({
        ...eventData,
        profiles: profileData || { full_name: null, email: 'Unknown', avatar_url: null },
        registrations: [{ count: count || 0 }]
      });
    } catch (error) {
      console.error('Error fetching event:', error);
      setEvent(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar forceScrolled />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-3 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-gray-400">Loading event...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar forceScrolled />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <i className="ri-calendar-close-line text-3xl text-gray-400"></i>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Event Not Found</h2>
            <p className="text-sm text-gray-500 mb-6">
              This event may have been removed or doesn&apos;t exist.
            </p>
            <Link
              to="/discover"
              className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500 text-white text-sm font-semibold rounded-lg hover:bg-teal-600 transition-colors"
            >
              <i className="ri-compass-line"></i>
              Browse Events
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const registrationCount = event.registrations?.[0]?.count ?? 0;
  const organizerName =
    event.profiles?.full_name ||
    event.profiles?.email.split('@')[0] ||
    'Unknown';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar forceScrolled />
      <EventHero
        title={event.title}
        category={event.category}
        coverImage={event.cover_image}
        coverImagePosition={(event as any).cover_image_position || 'center'}
        organizerName={organizerName}
        startDate={event.start_date}
        alreadyRegistered={alreadyRegistered}
        eventId={event.id}
        price={event.price}
        currency={event.currency}
        isFull={event.capacity ? (event.capacity - registrationCount) === 0 : false}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6 sm:space-y-8">
            <EventDescription description={event.description} />

            {/* Mobile mid-scroll CTA — only on small screens, not on desktop */}
            {!alreadyRegistered && (
              <div className="lg:hidden bg-gradient-to-r from-teal-500 via-emerald-500 to-cyan-500 rounded-2xl p-5 flex items-center justify-between gap-4">
                <div>
                  <p className="text-white font-bold text-base leading-tight">Ready to join?</p>
                  <p className="text-teal-100 text-xs mt-0.5">
                    {event.price && event.price > 0
                      ? `${event.currency || 'USD'} ${event.price.toFixed(2)}`
                      : 'Free event'}
                    {registrationCount > 0 && ` · ${registrationCount} attending`}
                  </p>
                </div>
                <button
                  onClick={() => {
                    if (!user) {
                      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                    } else {
                      navigate(`/event/${event.id}/register`);
                    }
                  }}
                  className="flex-shrink-0 px-5 py-2.5 bg-white text-teal-600 font-bold text-sm rounded-xl active:scale-95 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-ticket-line"></i>
                  Register
                </button>
              </div>
            )}

            <EventLocation
              locationType={event.location_type}
              locationAddress={event.location_address}
              locationCity={event.location_city}
              locationCountry={event.location_country}
              locationUrl={event.location_url}
              locationRevealDate={event.location_reveal_date}
            />
            <OrganizerCard
              name={organizerName}
              email={event.profiles?.email || ''}
              avatarUrl={event.profiles?.avatar_url}
              organizerId={event.organizer_id}
            />
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <EventInfoSidebar
              eventId={event.id}
              startDate={event.start_date}
              endDate={event.end_date}
              price={event.price}
              currency={event.currency}
              capacity={event.capacity}
              registrationCount={registrationCount}
              locationType={event.location_type}
              socialLinks={event.social_links}
              eventTitle={event.title}
              alreadyRegistered={alreadyRegistered}
            />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
